/**
 * SharedTripPage - Read-only view of a shared trip
 * Accessible without login via share code
 */

import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapPinned } from 'lucide-react';
import { getGoogleMapsUrl } from '../utils/googleMaps';
import { generateDaysFromBackend, formatDateRange, formatDateWithDay } from '../utils/dates';
import { convertUnplannedItineraries } from '../utils/itineraryConverter';
import { getDaySortKey, getDisplayTime } from '../utils/activityHelpers';
import { getPlacePhotoUrl } from '../utils/photoUrl';
import type { Activity } from '../types/activity';
import type { Day, TripDetailDTO } from '../types/trip';
import ActivityCard from '../components/activity/ActivityCard';
import TripMapView from '../components/trips/TripMapView';
import DesktopSidebar from '../components/common/DesktopSidebar';
import LoadingScreen from '../components/common/LoadingScreen';
import Modal from '../components/common/Modal';
import { getActivityTypeConfig } from '../utils/activityTypes';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

interface ShareResponse {
  exitCode: number;
  message: string;
  data: TripDetailDTO;
}


function computeDuration(activities: Activity[]): string {
  let totalMinutes = 0;
  for (const a of activities) {
    if (a.startTime && a.endTime) {
      const [sh, sm] = a.startTime.split(':').map(Number);
      const [eh, em] = a.endTime.split(':').map(Number);
      const diff = (eh * 60 + em) - (sh * 60 + sm);
      if (diff > 0) totalMinutes += diff;
    }
  }
  if (totalMinutes === 0) return '—';
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return m > 0 ? `${h} 小時 ${m} 分` : `${h} 小時`;
}

/** 分享頁專用的唯讀行程詳情內容（不依賴登入，只用已經拉回來的 tripDetail 資料組出畫面） */
function ReadOnlyActivityDetail({ activity }: { activity: Activity }) {
  const cfg = getActivityTypeConfig(activity.type);
  const photoUrl = getPlacePhotoUrl(activity.placeId);
  const displayTime = getDisplayTime(activity);

  const googleMapsUrl = getGoogleMapsUrl(activity);
  const handleOpenInMaps = () => {
    if (googleMapsUrl) window.open(googleMapsUrl, '_blank');
  };

  return (
    <div className="px-6 pb-6 flex flex-col gap-4 overflow-y-auto">
      <div className="h-48 w-full rounded-xl overflow-hidden bg-slate-100 shrink-0">
        {photoUrl ? (
          <img src={photoUrl} alt={activity.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center" style={{ background: cfg.bgColor }}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill={cfg.iconColor} opacity={0.4}>
              <path d={cfg.icon} />
            </svg>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        <div
          className="flex items-center justify-center rounded-full shrink-0"
          style={{ width: 28, height: 28, background: cfg.bgColor }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill={cfg.iconColor}>
            <path d={cfg.icon} />
          </svg>
        </div>
        <span className="text-xs font-medium" style={{ color: cfg.iconColor }}>{cfg.label}</span>
        {displayTime && <span className="text-xs text-slate-400">· {displayTime}</span>}
      </div>

      {activity.location && (
        <div>
          <p className="text-sm font-semibold text-slate-900 mb-1">地址</p>
          <p className="text-sm text-slate-500">{activity.location}</p>
        </div>
      )}

      {activity.note && (
        <div>
          <p className="text-sm font-semibold text-slate-900 mb-1">備註</p>
          <p className="text-sm text-slate-500 whitespace-pre-wrap">{activity.note}</p>
        </div>
      )}

      {googleMapsUrl && (
        <button
          onClick={handleOpenInMaps}
          className="flex items-center justify-center gap-2 rounded-xl bg-[#0dccf2] py-3 text-sm font-semibold text-white hover:bg-[#0bbfde] transition-colors"
        >
          <MapPinned size={16} />
          在地圖上開啟
        </button>
      )}
    </div>
  );
}

export default function SharedTripPage() {
  const { shareCode } = useParams<{ shareCode: string }>();
  const navigate = useNavigate();
  const [selectedDayIndex, setSelectedDayIndex] = useState(-1);
  const [tripDetail, setTripDetail] = useState<TripDetailDTO | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState<Activity | null>(null);
  const [selectedMapActivityId, setSelectedMapActivityId] = useState<string | null>(null);

  useEffect(() => {
    if (!shareCode) return;

    const loadSharedTrip = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`${API_BASE_URL}/trip/share/${shareCode}`);
        const json: ShareResponse = await response.json();

        if (json.exitCode === 1 && json.data) {
          setTripDetail(json.data);
        } else {
          setNotFound(true);
        }
      } catch {
        setNotFound(true);
      } finally {
        setIsLoading(false);
      }
    };

    loadSharedTrip();
  }, [shareCode]);

  const days: Day[] = useMemo(
    () => (tripDetail ? generateDaysFromBackend(tripDetail) : []),
    [tripDetail]
  );

  const unscheduledActivities = useMemo(() => {
    if (!tripDetail) return [];
    return convertUnplannedItineraries(tripDetail.unplannedItineraries, String(tripDetail.tripId));
  }, [tripDetail]);

  const selectedDay = selectedDayIndex >= 0 ? days[selectedDayIndex] : undefined;

  const sortedDayActivities = useMemo(() => {
    if (!selectedDay) return [];
    return [...selectedDay.activities].sort((a, b) =>
      getDaySortKey(a).localeCompare(getDaySortKey(b))
    );
  }, [selectedDay]);

  const dayDuration = useMemo(() => computeDuration(sortedDayActivities), [sortedDayActivities]);

  const dayNotes = useMemo(
    () => sortedDayActivities.filter((a) => a.note).map((a) => a.note as string),
    [sortedDayActivities]
  );

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (notFound || !tripDetail) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center gap-4">
        <p className="text-gray-500 text-lg">找不到此分享連結</p>
        <button
          onClick={() => navigate('/')}
          className="text-[#0dccf2] text-sm font-medium hover:underline"
        >
          回首頁
        </button>
      </div>
    );
  }

  const startDate = new Date(tripDetail.startDate);
  const endDate = new Date(tripDetail.endDate);

  const tabBar = (
    <div className="flex gap-3 px-4 lg:px-10 pb-3 min-w-max">
      <button
        onClick={() => setSelectedDayIndex(-1)}
        className={`flex h-9 shrink-0 items-center justify-center gap-x-2 rounded-full px-4 transition-all ${
          selectedDayIndex === -1
            ? 'bg-[#0dccf2] shadow-sm shadow-[#0dccf2]/20'
            : 'bg-white border border-slate-200 hover:border-[#0dccf2]/50'
        }`}
      >
        <svg
          className={`w-[18px] h-[18px] ${selectedDayIndex === -1 ? 'text-white' : 'text-slate-600'}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
        </svg>
        <span className={`text-sm font-bold leading-normal ${selectedDayIndex === -1 ? 'text-white' : 'text-slate-600'}`}>
          總覽
        </span>
      </button>
      {days.map((_, i) => (
        <button
          key={i}
          onClick={() => setSelectedDayIndex(i)}
          className={`flex h-9 shrink-0 items-center justify-center gap-x-2 rounded-full px-4 transition-colors ${
            selectedDayIndex === i
              ? 'bg-[#0dccf2] text-white'
              : 'bg-white border border-slate-200 hover:border-[#0dccf2]/50'
          }`}
        >
          <span className={`text-sm font-medium leading-normal ${selectedDayIndex === i ? 'text-white' : 'text-slate-600'}`}>
            Day {i + 1}
          </span>
        </button>
      ))}
    </div>
  );

  return (
    <div className="flex min-h-screen bg-[#f8f9fa]">
      <DesktopSidebar onNavigate={navigate} />
      <div className="flex-1 flex flex-col min-w-0 min-h-0">

        {/* ─── Mobile Header ─── */}
        <header className="lg:hidden sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-200">
          <div className="flex items-center p-4 justify-between">
            <button
              onClick={() => navigate('/')}
              className="text-slate-900 flex size-10 shrink-0 items-center justify-center rounded-full hover:bg-slate-100 transition-colors"
              aria-label="回首頁"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="text-slate-900 text-lg font-bold leading-tight tracking-tight flex-1 text-center">
              {tripDetail.name}
            </h1>
            <div className="w-10" />
          </div>
          <p className="text-slate-500 text-sm font-medium leading-normal pb-4 text-center">
            {formatDateRange(startDate, endDate)}
          </p>
          <div className="w-full overflow-x-auto scrollbar-hide border-b border-slate-200 bg-white">
            {tabBar}
          </div>
        </header>

        {/* ─── Desktop Header ─── */}
        <header className="hidden lg:flex flex-col bg-white border-b border-slate-100 sticky top-0 z-40">
          <div className="flex items-center justify-between px-10 py-5">
            <div className="flex items-center gap-4">
              <div>
                <h1 className="text-slate-900 text-xl font-bold leading-tight">{tripDetail.name}</h1>
                <p className="text-slate-400 text-sm mt-0.5">
                  {formatDateRange(startDate, endDate)}
                </p>
              </div>
            </div>
            {/* Read-only badge */}
            <div className="flex items-center gap-1.5 px-4 py-2 bg-slate-100 rounded-full text-slate-500 text-sm font-medium">
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              僅供瀏覽
            </div>
          </div>
          <div className="overflow-x-auto scrollbar-hide border-t border-slate-100">
            <div className="pt-3">{tabBar}</div>
          </div>
        </header>

        {/* ─── Mobile Content ─── */}
        <div className="lg:hidden flex-1 flex flex-col min-h-0">
          {selectedDayIndex === -1 ? (
            <main className="flex-1 w-full max-w-md mx-auto">
              <div className="px-4 pt-6 pb-2">
                <h2 className="text-slate-900 text-2xl font-bold leading-tight tracking-tight">總覽</h2>
                <p className="text-slate-500 text-sm font-medium">
                  未排期行程 • {unscheduledActivities.length} 個
                </p>
              </div>
              <div className="flex flex-col px-4 pt-4 pb-24">
                {unscheduledActivities.length === 0 ? (
                  <p className="text-slate-400 text-sm text-center py-8">沒有未排程的行程</p>
                ) : (
                  unscheduledActivities.map((activity) => (
                    <ActivityCard
                      key={activity.id}
                      activity={activity}
                      variant="unscheduled"
                      readOnly
                      onClick={() => setSelectedActivity(activity)}
                    />
                  ))
                )}
              </div>
            </main>
          ) : selectedDay ? (
            <main className="flex-1 w-full max-w-md mx-auto px-4 py-6 pb-24">
              <div className="mb-4">
                <h2 className="text-slate-900 text-2xl font-bold">Day {selectedDay.dayNumber}</h2>
                <p className="text-slate-500 text-sm">{formatDateWithDay(selectedDay.date)}</p>
              </div>
              {selectedDay.activities.length === 0 ? (
                <p className="text-slate-400 text-sm text-center py-8">這天還沒有行程</p>
              ) : (
                selectedDay.activities.map((activity, index) => (
                  <ActivityCard
                    key={activity.id}
                    activity={activity}
                    index={index}
                    variant="scheduled"
                    readOnly
                    onClick={() => setSelectedActivity(activity)}
                  />
                ))
              )}
            </main>
          ) : (
            <div className="text-center py-12 text-gray-500">找不到這一天的資料</div>
          )}
        </div>

        {/* ─── Desktop Content ─── */}
        <div className="hidden lg:flex flex-1 min-h-0">
          {selectedDayIndex === -1 ? (
            /* Desktop Overview — 3-column photo card grid */
            <main className="flex-1 px-10 py-8 overflow-y-auto">
              <div className="flex items-end justify-between mb-6">
                <div>
                  <h2 className="text-slate-900 text-2xl font-bold">總覽</h2>
                  <p className="text-slate-500 text-sm mt-1">
                    未排期行程 • {unscheduledActivities.length} 個
                  </p>
                </div>
              </div>
              {unscheduledActivities.length > 0 ? (
                <div className="grid grid-cols-3 gap-5">
                  {unscheduledActivities.map((activity) => {
                    const cfg = getActivityTypeConfig(activity.type);
                    const photoUrl = getPlacePhotoUrl(activity.placeId);
                    return (
                      <div
                        key={activity.id}
                        onClick={() => setSelectedActivity(activity)}
                        className="rounded-2xl overflow-hidden bg-white border border-[#E8E8E8] shadow-sm hover:shadow-md transition-all cursor-pointer flex flex-col"
                        style={{ boxShadow: '0 2px 6px rgba(0,0,0,0.03)' }}
                      >
                        <div className="h-40 w-full shrink-0 overflow-hidden bg-slate-100">
                          {photoUrl ? (
                            <img src={photoUrl} alt={activity.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center" style={{ background: cfg.bgColor }}>
                              <svg width="36" height="36" viewBox="0 0 24 24" fill={cfg.iconColor} opacity={0.4}>
                                <path d={cfg.icon} />
                              </svg>
                            </div>
                          )}
                        </div>
                        <div className="flex items-start gap-3.5 p-4">
                          <div
                            className="shrink-0 flex items-center justify-center rounded-full"
                            style={{ width: 44, height: 44, background: cfg.bgColor }}
                          >
                            <svg width="22" height="22" viewBox="0 0 24 24" fill={cfg.iconColor}>
                              <path d={cfg.icon} />
                            </svg>
                          </div>
                          <div className="flex flex-col gap-1.5 min-w-0">
                            <span className="text-[11px] font-semibold" style={{ color: cfg.iconColor }}>{cfg.label}</span>
                            <h3 className="text-base font-semibold text-slate-900 leading-tight">{activity.name}</h3>
                            {activity.location && (
                              <p className="text-[13px] text-[#999] leading-normal truncate">{activity.location}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-20 text-slate-400">
                  <p className="text-sm">尚無未排期行程</p>
                </div>
              )}
            </main>
          ) : selectedDay ? (
            /* Desktop Day View — left timeline + right summary */
            <>
              <main className="flex-1 min-w-0 overflow-y-auto px-10 py-8">
                <div className="mb-6">
                  <h2 className="text-slate-900 text-2xl font-bold leading-tight">
                    Day {selectedDay.dayNumber}
                  </h2>
                  <p className="text-slate-500 text-sm mt-1">
                    {formatDateWithDay(selectedDay.date)}
                    {sortedDayActivities.length > 0 && ` • ${sortedDayActivities.length} 個行程`}
                  </p>
                </div>

                {sortedDayActivities.length > 0 ? (
                  <div>
                    {sortedDayActivities.map((activity, index) => (
                      <ActivityCard
                        key={activity.id}
                        activity={activity}
                        index={index}
                        variant="scheduled"
                        readOnly
                        onClick={() => setSelectedActivity(activity)}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-16 text-slate-400">
                    <svg className="w-10 h-10 mb-3 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    <p className="text-sm">這天還沒有安排行程</p>
                  </div>
                )}
              </main>

              {/* Right: summary panel */}
              <aside className="w-80 shrink-0 border-l border-slate-100 bg-white overflow-y-auto">
                <div className="p-6 space-y-6">
                  {/* Day Summary */}
                  <div>
                    <h3 className="text-sm font-bold text-slate-900 mb-3">當日摘要</h3>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-[#f0fbff] rounded-xl p-3 text-center">
                        <p className="text-2xl font-bold text-[#0dccf2]">{sortedDayActivities.length}</p>
                        <p className="text-xs text-slate-500 mt-0.5">行程</p>
                      </div>
                      <div className="bg-slate-50 rounded-xl p-3 text-center">
                        <p className="text-2xl font-bold text-slate-700">{dayDuration}</p>
                        <p className="text-xs text-slate-500 mt-0.5">總時長</p>
                      </div>
                    </div>
                  </div>

                  {/* Map */}
                  <div>
                    <div className="rounded-xl overflow-hidden" style={{ height: 220 }}>
                      <TripMapView
                        activities={sortedDayActivities}
                        selectedActivityId={selectedMapActivityId}
                        onMarkerClick={(id) => setSelectedMapActivityId(id || null)}
                        onMapClick={() => setSelectedMapActivityId(null)}
                        onViewDetails={(activity) => setSelectedActivity(activity)}
                      />
                    </div>
                  </div>

                  {/* Notes */}
                  {dayNotes.length > 0 && (
                    <div>
                      <h3 className="text-sm font-bold text-slate-900 mb-2">備註</h3>
                      <ul className="space-y-1.5">
                        {dayNotes.map((note, i) => (
                          <li key={i} className="flex items-start gap-2 text-xs text-slate-600">
                            <span className="text-[#0dccf2] mt-0.5 shrink-0">•</span>
                            <span>{note}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </aside>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-500">找不到這一天的資料</div>
          )}
        </div>
      </div>

      {/* Read-only badge (mobile only) */}
      <div className="lg:hidden fixed bottom-6 left-1/2 -translate-x-1/2 z-50">
        <div className="flex items-center gap-1.5 px-4 py-2 bg-slate-800/80 backdrop-blur-sm rounded-full text-white text-xs font-medium">
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
          僅供瀏覽
        </div>
      </div>

      {/* 唯讀行程詳情彈窗 */}
      <Modal
        isOpen={!!selectedActivity}
        onClose={() => setSelectedActivity(null)}
        variant="bottom-sheet"
        title={selectedActivity?.name}
      >
        {selectedActivity && <ReadOnlyActivityDetail activity={selectedActivity} />}
      </Modal>
    </div>
  );
}
